import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Pagination,
  IconButton,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Drafts as DraftsIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { coursesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UIKit/Button';
import Select from '../components/UIKit/Select';
import SearchInput from '../components/UIKit/SearchInput';
import CourseCard from '../components/CourseCard';
import CourseJsonImport from '../components/UIKit/CourseJsonImport';
import CustomAutocomplete from '../components/UIKit/CustomAutocomplete';
import { useFilters } from '../context/FiltersContext';
import libraryEventsService from '../services/libraryEventsService';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading, updateProfession, deleteProfession } from '../redux/slices/professionsSlice';
import { fetchTools, selectAllTools, selectToolsLoading, updateTool, deleteTool } from '../redux/slices/toolsSlice';

const Courses = () => {
  const { t } = useTranslation('courses');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuth();
  const { filters, updateFilters } = useFilters();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showDrafts, setShowDrafts] = useState(false);
  const [showMyCourses, setShowMyCourses] = useState(false);
  
  // Используем данные из Redux вместо локального состояния
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const tools = useSelector(selectAllTools);
  const toolsLoading = useSelector(selectToolsLoading);

  // Загрузка профессий и инструментов при монтировании компонента
  useEffect(() => {
    dispatch(fetchProfessions({ all: true, isCommon:1 }));
    dispatch(fetchTools({ all: true, isCommon: 1 }));
  }, [dispatch]);

  // Подписка на события библиотек
  useEffect(() => {
    // Обработчик событий профессий
    const handleProfessionEvent = (action, data) => {
      console.log(`Получено событие профессии: ${action}`, data);
      
      // Используем экшены Redux для обновления профессий
      if (action === 'create' || action === 'update') {
        dispatch(updateProfession(data));
      } else if (action === 'delete') {
        // Извлекаем ID профессии из разных возможных форматов
        let professionId = null;
        
        if (data && data.id) {
          professionId = data.id;
        } else if (typeof data === 'object' && Object.keys(data).length === 1) {
          // Возможно, это формат {id: '29'}
          professionId = Object.values(data)[0];
        }
        
        console.log('Компонент Courses: удаление профессии с ID:', professionId);
        
        if (professionId) {
          dispatch(deleteProfession(professionId));
          
          // Удаляем профессию из выбранных фильтров, если она была выбрана
          if (filters.professions && filters.professions.length > 0) {
            const profIdStr = professionId.toString();
            
            const updatedProfessions = filters.professions.filter(p => {
              const pId = typeof p === 'object' ? (p.id || p._id) : p;
              return pId?.toString() !== profIdStr;
            });
            
            console.log('Фильтры до:', filters.professions);
            console.log('Фильтры после:', updatedProfessions);
            
            if (updatedProfessions.length !== filters.professions.length) {
              console.log('Обновляем фильтры после удаления профессии');
              updateFilters({
                ...filters,
                professions: updatedProfessions
              });
            }
          }
        } else {
          console.error('Не удалось извлечь ID профессии из данных события:', data);
        }
      }
    };

    // Обработчик событий инструментов
    const handleToolEvent = (action, data) => {
      console.log(`Получено событие инструмента: ${action}`, data);

      if (action === 'create' || action === 'update') {
        dispatch(updateTool(data));
      } else if (action === 'delete') {
        let toolId = null;

        if (data && data.id) {
          toolId = data.id;
        } else if (typeof data === 'object' && Object.keys(data).length === 1) {
          toolId = Object.values(data)[0];
        }

        if (toolId) {
          dispatch(deleteTool(toolId));

          // Удаляем инструмент из выбранных фильтров, если он был выбран
          if (filters.tools && filters.tools.length > 0) {
            const toolIdStr = toolId.toString();

            const updatedTools = filters.tools.filter(t => {
              const tId = typeof t === 'object' ? (t.id || t._id) : t;
              return tId?.toString() !== toolIdStr;
            });

            if (updatedTools.length !== filters.tools.length) {
              updateFilters({
                ...filters,
                tools: updatedTools
              });
            }
          }
        }
      }
    };

    // Подписываемся на события профессий и инструментов
    const unsubscribeProfession = libraryEventsService.subscribe('profession', handleProfessionEvent);
    const unsubscribeTool = libraryEventsService.subscribe('tool', handleToolEvent);

    // Отписываемся при размонтировании компонента
    return () => {
      unsubscribeProfession();
      unsubscribeTool();
    };
  }, [filters, updateFilters, dispatch]);

  // Обработчики изменения фильтров
  const handleSearchChange = (value) => {
    updateFilters({ ...filters, search: value, page: 1 });
  };

  // Обработчик удаления профессии из списка выбранных
  const handleDeleteProfession = (indexToDelete) => {
    const newProfessions = [...filters.professions];
    newProfessions.splice(indexToDelete, 1);
    console.log(
      'Deleting profession at index:',
      indexToDelete,
      'new list:',
      newProfessions
    );

    updateFilters({
      ...filters,
      professions: newProfessions,
      page: 1,
    });
  };

  const handleProfessionsChange = (event, newValue) => {
    console.log('newValue', newValue);
    updateFilters({
      ...filters,
      professions: Array.isArray(newValue) ? newValue : [],
      page: 1,
    });
  };

  // Обработчик удаления инструмента из списка выбранных
  const handleDeleteTool = (indexToDelete) => {
    const newTools = [...filters.tools];
    newTools.splice(indexToDelete, 1);
    updateFilters({
      ...filters,
      tools: newTools,
      page: 1,
    });
  };

  const handleToolsChange = (event, newValue) => {
    updateFilters({
      ...filters,
      tools: Array.isArray(newValue) ? newValue : [],
      page: 1,
    });
  };

  const handleLevelChange = (e) => {
    updateFilters({ ...filters, level: e.target.value, page: 1 });
  };

  const handleSortChange = (e) => {
    updateFilters({ ...filters, sortBy: e.target.value, page: 1 });
  };

  const handleContentTypeChange = (e) => {
    updateFilters({ ...filters, contentType: e.target.value, page: 1 });
  };

  const handlePageChange = (event, value) => {
    updateFilters({ ...filters, page: value });
  };

  // Загрузка курсов с учетом фильтров
  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: filters.page,
        limit: 8,
        sortBy: filters.sortBy,
      };

      // Если выбраны профессии
      if (filters.professions && filters.professions.length > 0) {
        params.professions = filters.professions
          .map((prof) =>
            typeof prof === 'object' ? prof.id || prof._id : prof
          )
          .join(',');
      }

      // Если выбраны инструменты
      if (filters.tools && filters.tools.length > 0) {
        params.tools = filters.tools
          .map((tool) =>
            typeof tool === 'object' ? tool.id || tool._id : tool
          )
          .join(',');
      }

      if (filters.level !== 'all') {
        params.difficulty = filters.level;
      }

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.contentType && filters.contentType !== '') {
        params.contentType = filters.contentType;
      }

      // Добавляем параметр для отображения черновиков
      if (showDrafts) {
        params.showDrafts = true;
      }

      // Добавляем параметр для отображения курсов пользователя
      if (showMyCourses) {
        params.myCourses = true;
      }

      const response = await coursesAPI.getAll(params);
      setCourses(response.data.courses || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      setError('Ошибка при загрузке курсов');
      console.error('Ошибка при загрузке курсов:', err);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [filters, showDrafts, showMyCourses]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleCourseImportSuccess = (importedCourse) => {
    // После успешного импорта курса, обновляем список курсов
    fetchCourses();
    // Опционально можно перенаправить на страницу редактирования импортированного курса
    if (importedCourse && (importedCourse.slug || importedCourse._id)) {
      navigate(`/courses/${importedCourse.slug || importedCourse._id}/edit`);
    }
  };

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.professions.length > 0 ||
      filters.tools.length > 0 ||
      filters.level !== 'all' ||
      filters.sortBy !== 'newest' ||
      filters.contentType !== '' ||
      filters.page !== 1
    );
  }, [filters]);

  // Обработчик переключения отображения расширенных фильтров
  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters(!showAdvancedFilters);
  };

  // Обработчик очистки всех фильтров
  const handleClearFilters = () => {
    updateFilters({
      search: '',
      professions: [],
      tools: [],
      level: 'all',
      sortBy: 'newest',
      contentType: '',
      page: 1,
    });
  };

  // Обработчик переключения между обычными курсами и черновиками
  const toggleDrafts = () => {
    setShowDrafts(!showDrafts);
    // Сбрасываем страницу на первую при переключении режима
    updateFilters({
      ...filters,
      page: 1
    });
  };

  // Обработчик переключения отображения курсов пользователя
  const toggleMyCourses = () => {
    setShowMyCourses(!showMyCourses);
    // Сбрасываем страницу на первую при переключении режима
    updateFilters({
      ...filters,
      page: 1
    });
  };

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography component="h1" variant="h3">
            {t('coursesPage.title')}
          </Typography>
          {isAuthenticated && user && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Кнопка "Show Drafts" для admin и teacher */}
              {(user.userMeta.roles.includes('admin') || user.userMeta.roles.includes('teacher')) && (
                <Button
                  variant={showDrafts ? "contained" : "outlined"}
                  color={showDrafts ? "warning" : "primary"}
                  onClick={toggleDrafts}
                  startIcon={showDrafts ? <VisibilityIcon /> : <DraftsIcon />}
                  data-testid="toggle-drafts-button"
                >
                  {showDrafts ? t('coursesPage.showPublished') : t('coursesPage.showDrafts')}
                </Button>
              )}
              
              {/* Кнопка "Мои курсы" для всех авторизованных пользователей */}
              <Button
                variant={showMyCourses ? "contained" : "outlined"}
                color={showMyCourses ? "secondary" : "primary"}
                onClick={toggleMyCourses}
                data-testid="toggle-my-courses-button"
              >
                {showMyCourses ? t('coursesPage.showAllCourses') : t('myCourses.title')}
              </Button>
              
              {/* Кнопка "Import JSON" для admin и teacher */}
              {(user.userMeta.roles.includes('admin') || user.userMeta.roles.includes('teacher')) && (
                <CourseJsonImport onSuccess={handleCourseImportSuccess} />
              )}
              
              {/* Кнопка создания курса для всех авторизованных пользователей */}
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/courses/create')}
                data-testid="create-button"
              >
                {t('coursesPage.addCourse')}
              </Button>
            </Box>
          )}
        </Box>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          {t('coursesPage.subtitle')}
        </Typography>

        {/* Фильтры и поиск */}
        <Box sx={{ mb: hasActiveFilters ? 0 : 4 }} className="filters">
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 2,
              alignItems: 'center',
            }}
          >
            <SearchInput
              value={filters.search}
              onChange={handleSearchChange}
              placeholder={t('coursesPage.searchPlaceholder')}
              sx={{ flex: 1 }}
            />

            <IconButton
              onClick={toggleAdvancedFilters}
              sx={{
                bgcolor: showAdvancedFilters ? 'primary.main' : 'background.paper',
                color: showAdvancedFilters ? 'white' : 'text.primary',
                border: 1,
                borderColor: showAdvancedFilters ? 'primary.main' : 'divider',
                borderRadius: 2,
                width: 48,
                height: 48,
                '&:hover': {
                  bgcolor: showAdvancedFilters ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <FilterListIcon />
            </IconButton>

            {hasActiveFilters && (
              <IconButton
                onClick={handleClearFilters}
                sx={{
                  bgcolor: 'background.paper',
                  color: 'error.main',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  width: 48,
                  height: 48,
                  '&:hover': {
                    bgcolor: 'error.light',
                    color: 'white',
                    borderColor: 'error.main',
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          {/* Расширенные фильтры */}
          {showAdvancedFilters && (
            <Box sx={{
              mb: 3,
              p: 3,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: 3,
            }}>
              {/* Grid для первых двух фильтров */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                mb: 2
              }}>
                {/* Профессии */}
                <CustomAutocomplete
                  placeholder={t('coursesPage.filters.selectProfessions')}
                  options={professionsLoading ? [] : professions}
                  loading={professionsLoading}
                  onChange={(newValue) => {
                    handleProfessionsChange(null, newValue);
                  }}
                  getOptionLabel={(option) =>
                    option.library && option.library.name
                      ? option.library.name
                      : option.name || ''
                  }
                  selectedChips={filters.professions || []}
                  onChipDelete={handleDeleteProfession}
                  chipColor="primary"
                  zIndex={20}
                  openDirection="down"
                />

                {/* Инструменты */}
                <CustomAutocomplete
                  placeholder={t('coursesPage.filters.selectTools')}
                  options={toolsLoading ? [] : tools}
                  loading={toolsLoading}
                  onChange={(newValue) => {
                    handleToolsChange(null, newValue);
                  }}
                  getOptionLabel={(option) =>
                    option.term_group?.MainTerm?.value ||
                    option.library?.name ||
                    option.name ||
                    ''
                  }
                  selectedChips={filters.tools || []}
                  onChipDelete={handleDeleteTool}
                  chipColor="secondary"
                  zIndex={20}
                  openDirection="down"
                />
              </Box>

              {/* Grid для трех селектов */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: 2
              }}>
                {/* Уровень */}
                <Select
                  value={filters.level}
                  onChange={handleLevelChange}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="all">
                    {t('coursesPage.filters.allLevels')}
                  </MenuItem>
                  <MenuItem value="beginner">
                    {t('coursesPage.filters.beginner')}
                  </MenuItem>
                  <MenuItem value="intermediate">
                    {t('coursesPage.filters.intermediate')}
                  </MenuItem>
                  <MenuItem value="advanced">
                    {t('coursesPage.filters.advanced')}
                  </MenuItem>
                </Select>

                {/* Тип контента */}
                <Select
                  value={filters.contentType}
                  onChange={handleContentTypeChange}
                  size="small"
                  fullWidth
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected === '') {
                      return <span style={{ color: 'white' }}>{t('coursesPage.filters.allContentTypes')}</span>;
                    }
                    // Special handling for no-video and no-presentation filters
                    if (selected === 'no-video') {
                      return t('coursesPage.filters.noVideo');
                    }
                    if (selected === 'no-presentation') {
                      return t('coursesPage.filters.noPresentation');
                    }
                    // For other content types, use lessons namespace
                    return t(`lessons:contentTypes.${selected}.name`);
                  }}
                >
                  <MenuItem value="">
                    {t('coursesPage.filters.allContentTypes')}
                  </MenuItem>
                  <MenuItem value="standard">
                    {t('lessons:contentTypes.standard.name')}
                  </MenuItem>
                  <MenuItem value="video">
                    {t('lessons:contentTypes.video.name')}
                  </MenuItem>
                  <MenuItem value="no-video">
                    {t('coursesPage.filters.noVideo')}
                  </MenuItem>
                  <MenuItem value="presentation">
                    {t('lessons:contentTypes.presentation.name')}
                  </MenuItem>
                  <MenuItem value="no-presentation">
                    {t('coursesPage.filters.noPresentation')}
                  </MenuItem>
                  <MenuItem value="labyrinth">
                    {t('lessons:contentTypes.labyrinth.name')}
                  </MenuItem>
                  <MenuItem value="flippingCards">
                    {t('lessons:contentTypes.flippingCards.name')}
                  </MenuItem>
                  <MenuItem value="memoryGame">
                    {t('lessons:contentTypes.memoryGame.name')}
                  </MenuItem>
                  <MenuItem value="tagCloud">
                    {t('lessons:contentTypes.tagCloud.name')}
                  </MenuItem>
                  <MenuItem value="rolePlayGame">
                    {t('lessons:contentTypes.rolePlayGame.name')}
                  </MenuItem>
                  <MenuItem value="mixed">
                    {t('lessons:contentTypes.mixed.name')}
                  </MenuItem>
                  <MenuItem value="fullHtml">
                    {t('lessons:contentTypes.fullHtml.name')}
                  </MenuItem>
                  <MenuItem value="htmlBlock">
                    {t('lessons:contentTypes.htmlBlock.name')}
                  </MenuItem>
                  <MenuItem value="textReconstruction">
                    {t('lessons:contentTypes.textReconstruction.name')}
                  </MenuItem>
                </Select>

                {/* Сортировка */}
                <Select
                  value={filters.sortBy}
                  onChange={handleSortChange}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="newest">
                    {t('coursesPage.filters.sortBy.newest')}
                  </MenuItem>
                  <MenuItem value="oldest">
                    {t('coursesPage.filters.sortBy.oldest')}
                  </MenuItem>
                  <MenuItem value="rating">
                    {t('coursesPage.filters.sortBy.rating')}
                  </MenuItem>
                  <MenuItem value="popularity">
                    {t('coursesPage.filters.sortBy.popularity')}
                  </MenuItem>
                </Select>
              </Box>
            </Box>
          )}
        </Box>

        {/* Список курсов */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
            <CircularProgress />
            {filters.contentType && filters.contentType !== '' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('coursesPage.filters.analyzingContentTypes')}...
              </Typography>
            )}
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography variant="h6" color="error">
              {t('coursesPage.errors.loadError')}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={fetchCourses}
              sx={{ mt: 2 }}
            >
              {t('coursesPage.errors.tryAgain')}
            </Button>
          </Box>
        ) : courses.length === 0 ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography variant="h6">
              {t('coursesPage.noCourses.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('coursesPage.noCourses.subtitle')}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              className="catalog"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 3,
              }}
            >
              {courses.map((course) => (
                <CourseCard key={course._id} course={course} />
              ))}
            </Box>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={filters.page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default Courses;
